import { createContext, useContext, type ReactNode } from 'react'

import { useAuthStore } from '@/features/auth/store/authStore'

import { useChatStream } from '../hooks/useChatStream'
import type { ChatStreamState } from '../types'

/**
 * Contexto singleton del stream SSE del chat.
 *
 * <p>Monta UNA conexión al endpoint /me/chat-stream en el árbol y expone su
 * {@link ChatStreamState} a los consumers ({@code useChatMessagesQuery},
 * {@code useMyChatsQuery}) para que reactiven polling cuando caemos en
 * {@code POLLING_FALLBACK}.</p>
 *
 * <p>El JWT lo leemos del store de auth ({@code accessToken}). Si cambia
 * (login/logout/refresh) el {@code useEffect} interno del hook se re-suscribe
 * porque tiene {@code opts.jwt} en deps. Si es {@code null}, el hook devuelve
 * {@code 'CLOSED'} y no abre conexión.</p>
 */

const Ctx = createContext<ChatStreamState>('CLOSED')

export function ChatStreamProvider({ children }: { children: ReactNode }) {
  const jwt = useAuthStore((s) => s.accessToken)
  // Mismo env var que el httpClient axios (VITE_API_BASE_URL). En local es
  // '/api/v1' (Vite proxy) y en dev/prod es 'https://api.[dev.]matchplay.es/api/v1'.
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
  const state = useChatStream({ jwt, apiBase })
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>
}

// Provider + hook conviven en este archivo a propósito (patrón típico de
// React Context). El eslint disable cubre la "warning" de fast-refresh por
// tener un hook y un componente en el mismo módulo.
// eslint-disable-next-line react-refresh/only-export-components
export function useChatStreamState(): ChatStreamState {
  return useContext(Ctx)
}
